import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import fragranceNoteService from '../../services/fragranceNoteService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';

const FragranceNotesIndex = () => {
  const { get, isRtl } = useLocalization();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      setIsLoading(true);
      const data = await fragranceNoteService.getAll();
      setNotes(data);
    } catch (error) {
      console.error('Error loading fragrance notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذه الملاحظة؟' : 'Are you sure you want to delete this note?')) {
      try {
        await fragranceNoteService.delete(id);
        loadNotes();
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
  };

  const filteredNotes = notes.filter(note => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      note.nameEn?.toLowerCase().includes(search) ||
      note.nameAr?.toLowerCase().includes(search) ||
      note.type?.toLowerCase().includes(search)
    );
  });

  const columns = [
    { header: isRtl ? 'الاسم (إنجليزي)' : 'Name (English)', accessor: 'nameEn' },
    { header: isRtl ? 'الاسم (عربي)' : 'Name (Arabic)', accessor: 'nameAr' },
    { header: isRtl ? 'النوع' : 'Type', accessor: 'type' },
    {
      header: isRtl ? 'عدد الاستخدامات' : 'Usage Count',
      accessor: 'usageCount',
      render: (value) => value || 0,
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (value) => (
        <Button size="small" variant="danger" onClick={() => handleDelete(value)}>
          {get('delete')}
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading fragrance notes...'} />;
  }

  return (
    <div className="fragrance-notes-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إدارة ملاحظات العطر' : 'Fragrance Notes Management'}</h1>
            <p className="page-description">{isRtl ? 'إدارة اللبنات الأساسية لملفات العطور' : 'Manage the building blocks of your perfume scent profiles'}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={() => navigate('/fragrance-notes/create')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {get('add_new_note')}
            </Button>
          </div>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{notes.length}</div>
              <div className="stat-label">{isRtl ? 'إجمالي الملاحظات' : 'Total Notes'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{notes.filter(n => (n.usageCount || 0) > 0).length}</div>
              <div className="stat-label">{isRtl ? 'ملاحظات نشطة' : 'Active Notes'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{notes.reduce((sum, n) => sum + (n.usageCount || 0), 0)}</div>
              <div className="stat-label">{isRtl ? 'إجمالي الاستخدامات' : 'Total Usage'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن الملاحظات...' : 'Search fragrance notes...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {isRtl ? `${filteredNotes.length} ملاحظة` : `${filteredNotes.length} notes`}
          </span>
        </div>
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredNotes}
            emptyMessage={isRtl ? 'لا توجد ملاحظات عطر' : 'No fragrance notes found'}
          />
        </div>
      </div>
    </div>
  );
};

export default FragranceNotesIndex;
